import { useState } from "react";
import { Card } from "@tremor/react";
import { cn } from "@/lib/utils";
import {
  useIncidents, useIncidentSummary,
  type Incident, type Severity,
} from "@/lib/queries";

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-500/10 text-red-600 border-red-500/20 dark:text-red-400",
  high:     "bg-orange-500/10 text-orange-600 border-orange-500/20 dark:text-orange-400",
  medium:   "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400",
  low:      "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400",
  info:     "bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400",
};

const SEVERITY_TILE: Record<string, string> = {
  critical: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
  high:     "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20",
  medium:   "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  low:      "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  info:     "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
};

// Friendly labels for the kinds we actually emit. Anything not in the map
// renders verbatim (so new sources show up without a code change).
const KIND_LABEL: Record<string, string> = {
  ssh_session:   "SSH sessions",
  imunify_event: "Imunify",
  mail_reject:   "Mail rejects",
  zabbix:        "Zabbix",
  panel_login:   "Panel logins",
  malware:       "Malware",
};

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function shortClusterKey(ck: Incident["cluster_key"]): string {
  if (!ck) return "";
  // cluster_key arrives as a JSON object (jsonb) describing what makes
  // this cluster unique — e.g. {"username": "root"} or {"port": 25}.
  // We render it as a "key=value, key=value" pill row.
  const obj = typeof ck === "string" ? safeParse(ck) : ck;
  if (!obj || typeof obj !== "object") return "";
  return Object.entries(obj)
    .filter(([, v]) => v !== null && v !== "")
    .map(([k, v]) => `${k}=${String(v)}`)
    .join(" · ");
}

function safeParse(s: string): unknown {
  try { return JSON.parse(s); } catch { return null; }
}

export default function Incidents() {
  const [severityMin, setSeverityMin] = useState<number | null>(null);
  const [kind, setKind] = useState<string | "all">("all");
  const [status, setStatus] = useState<"open" | "closed">("open");

  const summary = useIncidentSummary(status);
  const q = useIncidents({
    status,
    kind: kind === "all" ? undefined : kind,
    severity_min: severityMin ?? undefined,
    limit: 200,
  });

  const labelFor = (k: string) => KIND_LABEL[k] ?? k;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Incidents</h1>
        <p className="text-sm text-muted-foreground">
          Clustered events from Graylog, Zabbix, Imunify, and SSH session tracking.
          {status === "open" ? " Open incidents only." : " Closed incidents."}
        </p>
      </div>

      {/* Severity tiles */}
      {summary.data && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {(["critical", "high", "medium", "low", "info"] as Severity[]).map((s, i) => {
            const sevInt = 4 - i;
            const isActive = severityMin === sevInt;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setSeverityMin(isActive ? null : sevInt)}
                className={cn(
                  "rounded-lg border px-4 py-3 text-left transition-shadow hover:shadow-sm",
                  SEVERITY_TILE[s],
                  isActive && "ring-2 ring-current",
                )}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{s}</div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">
                  {summary.data.by_severity[s] ?? 0}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Kind tabs */}
      {summary.data && summary.data.by_kind.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
          <button
            type="button"
            onClick={() => setKind("all")}
            className={cn(
              "flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors",
              kind === "all"
                ? "border-primary bg-primary/5 text-foreground"
                : "border-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
          >
            <span className="font-medium">All sources</span>
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
              {summary.data.total}
            </span>
          </button>

          {summary.data.by_kind.map((k) => (
            <button
              key={k.kind}
              type="button"
              onClick={() => setKind(k.kind)}
              className={cn(
                "flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors",
                kind === k.kind
                  ? "border-primary bg-primary/5 text-foreground"
                  : "border-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              <span className="font-medium">{labelFor(k.kind)}</span>
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
                {k.total}
              </span>
              {k.critical > 0 && (
                <span className="rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-red-600 dark:text-red-400">
                  {k.critical} crit
                </span>
              )}
            </button>
          ))}

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setStatus("open")}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                status === "open"
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-muted-foreground hover:bg-accent",
              )}
            >open</button>
            <button
              type="button"
              onClick={() => setStatus("closed")}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                status === "closed"
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-muted-foreground hover:bg-accent",
              )}
            >closed</button>
          </div>
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Severity</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Last event</th>
              <th className="px-4 py-3">Host</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3 text-right">Events</th>
              <th className="px-4 py-3">Detail</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</td></tr>
            )}
            {q.isError && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-red-500">Failed to load incidents.</td></tr>
            )}
            {q.data?.items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No incidents match the current filters.</td></tr>
            )}
            {q.data?.items.map((i: Incident) => (
              <tr key={i.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide",
                      SEVERITY_STYLES[i.severity],
                    )}
                  >{i.severity}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{i.kind}</td>
                <td className="px-4 py-3 tabular-nums text-muted-foreground">{fmtTime(i.last_event_at)}</td>
                <td className="px-4 py-3 text-foreground">{i.hostname ?? "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  {i.actor ? `${i.actor_kind === "ip" ? "" : i.actor_kind + ":"}${i.actor}` : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{i.event_count.toLocaleString()}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{shortClusterKey(i.cluster_key)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
